import { useState, useRef, useEffect } from 'react'

import { useModuleContext } from '../contexts/ModuleContext'
import useCustomLaunchData from './useCustomLaunchData'

import { isDebug } from '../App.config'
import { simMod } from '../Module.config'
import { getModuleScore, isCourseComplete } from '../LMS'
import { TS_Profile } from '../ModuleTypes'
import { MasteryLevelName, masteryLevels } from '../Types'

import { toBoolean } from '../Utils'
import { LMSContextActionType, useLMSContext } from '../contexts/LMSContext'

/**
 * @description An enum that defines the available settings used in a {@link CourseConfig}.
 * @category Content Controller
 * @readonly
 * @enum {string} Name of the setting.
 */
enum CourseConfigSetting {
    CompleteOnLevel = 'CompleteOnLevel',
    CompleteOnFirstFault = 'CompleteOnFirstFault'
}

/** 
 * @category Content Controller
 * @typedef {Object} CourseConfig Creates a new type named 'CourseConfig' defining the shape
 * of the available {@link CourseConfigSetting} settings.  **Only one of the CompleteOn.. 
 * settings can be configured in the Content Controller, otherwise, an error will occur.**
 * @property {(MasteryLevelName | undefined)} CompleteOnLevel 
 * Defines a Mastery Level (aka Achievement level) at which to mark the course as complete.
 * Once the user achieves this level, the course in the LMS will be marked as Passed even if there 
 * are still higher levels to complete.
 * @example <caption>Example configuration of 'CompleteOnLevel' in the Content Controller. Note
 * that double-quotes MUST be used.</caption>
 * {
 *  "CompleteOnLevel": "Advanced"
 * }
 * @property {boolean} CompleteOnFirstFault 
 * Determines whether or not to mark the course as complete
 * on the first fault attempt. The default is 'false' if the setting is not configured.
 * @example <caption>Example configuration of 'CompleteOnFirstFault' in the Content Controller. 
 * Note that double-quotes MUST be used.</caption>
 * {
 *  "CompleteOnFirstFault": true
 * }
 * @example <caption>Example of an **invalid configuration** which uses both CompleteOn.. settings 
 * and **will cause an error**.</caption>
 * **INVALID CONFIGURATION!**
 * {
 *  "CompleteOnFirstFault": true,
 *  "CompleteOnLevel": "Advanced"
 * }
 */
export type CourseConfig = {
    [CourseConfigSetting.CompleteOnLevel]: MasteryLevelName | undefined
    [CourseConfigSetting.CompleteOnFirstFault]: boolean
}

/** 
 * @description 
 * This React Hook creates a {@link CourseConfig} object with each setting
 * in {@link CourseConfigSetting}. It then saves this object as the course's
 * configuration settings in the LMS Context. It can then be accessed by 
 * the 'courseConfig' state value of the 'lmsState' in the LMS Context 
 * anywhere the app consumes the context. **Accessing the settings directly is
 * NOT recommended outside of this hook.**
 * 
 * It must be called as soon as possible since the Custom Launch Data URL 
 * expires in a short amount of time.   
 * @category Content Controller
 * @example <caption>Using the 'shouldMarkAsComplete' returned value.</caption>
 * import useCourseConfig from 'useCourseConfig'
 * 
 * const {shouldMarkAsComplete} = useCourseConfig() // This will also instantiate it in the LMS Context state.
 * 
 * useEffect(() => {
 *     if (shouldMarkAsComplete) {
 *         LMS.setModulePassed()
 *     }
 * }, [shouldMarkAsComplete])
 * 
 * @example <caption>Using it to instantiate on App initialization. You can also access the 
 * 'courseConfig' state value directly, but it is NOT recommended outside of this hook.</caption>
 * // App.tsx
 * import useCourseConfig from 'useCourseConfig'
 * 
 * useCourseConfig() // You don't need to destructure the returned value in this case. 
 * 
 * // Some other file.
 * const { lmsState: { courseConfig }} = useLMSContext()
 * 
 * useEffect(() => {
 *     if (!courseConfig) return
 *  
 *     if(courseConfig.CompleteOnFirstFault) {  // Accessing the setting directly
 *          // Do something
 *     }
 * }, [courseConfig])
 * @returns {object} An object with a single boolean 'shouldMarkAsComplete' to determine
 * whether or not you can and should update the course status to 'Passed'. This will be
 * TRUE if all the following conditions are TRUE:
 *
 * * A 'CompleteOn' setting is configured
 * * LMS is available
 * * Module is not a Lab
 * * Course is not already passed or completed
 * * Condition of the setting is met
 */
const useCourseConfig = () => {
    const { userProfile } = useModuleContext()
    const { lmsState: { courseConfig, isLMSAvailable }, lmsDispatch } = useLMSContext()
    const { isLoaded, getValue: getSetting } = useCustomLaunchData()

    const [shouldMarkAsComplete, setShouldMarkAsComplete] = useState(false)
    const fetched = useRef(false)

    useEffect(() => {
        if (!isLoaded || courseConfig || fetched.current) return
        fetched.current = true
        const completeOnLevel = getSetting(CourseConfigSetting.CompleteOnLevel)
        const completeOnFirstFault = getSetting(CourseConfigSetting.CompleteOnFirstFault)

        if (completeOnLevel && completeOnFirstFault)
            throw new Error(`Custom Launch Data misconfiguration. Must not set both ${CourseConfigSetting.CompleteOnFirstFault} and ${CourseConfigSetting.CompleteOnFirstFault} `)

        const level = masteryLevels.filter(lvl => lvl.name === completeOnLevel)
        const levelValue = level.length ? level[0].name : undefined

        const config = {
            CompleteOnLevel: levelValue,
            CompleteOnFirstFault: toBoolean(completeOnFirstFault)
        } as CourseConfig

        lmsDispatch({ type: LMSContextActionType.SET_COURSE_CONFIG, payload: config })
    }, [courseConfig, isLoaded, getSetting, lmsDispatch])

    const isTSProfile = 'module-level-achieved' in userProfile

    useEffect(() => {
        if (!courseConfig?.CompleteOnLevel
            || !isLMSAvailable
            || simMod.isLab
            || !isTSProfile
        ) return

        if (isCourseComplete()) return

        const profile = userProfile as TS_Profile
        if (!profile['module-level-achieved']) return

        const levelAchieved = masteryLevels.filter(lvl => lvl.level === profile['module-level-achieved'])[0].name

        if (levelAchieved !== courseConfig.CompleteOnLevel || !levelAchieved || !courseConfig.CompleteOnLevel)
            return

        isDebug && console.log(`Course configuration says to mark course complete on level ${courseConfig.CompleteOnLevel}`)
        setShouldMarkAsComplete(true)

    }, [isLMSAvailable, courseConfig, isTSProfile, userProfile])

    useEffect(() => {
        if (!courseConfig?.CompleteOnFirstFault
            || !isLMSAvailable
            || simMod.isLab
            || isCourseComplete()
            || getModuleScore() < 1
        ) return

        isDebug && console.log(`Course configuration says to mark course complete on first fault: ${courseConfig.CompleteOnFirstFault}`)
        setShouldMarkAsComplete(true)

    }, [isLMSAvailable, courseConfig?.CompleteOnFirstFault])

    return { shouldMarkAsComplete }
}
export default useCourseConfig

